/**
 * Viewing Mode Configuration System
 *
 * Provides config-driven viewing experience variants with adaptive smart defaults.
 * This system implements a PRIORITY-BASED model where paid users get faster service.
 *
 * ## Priority Model (Corrected Vision)
 *
 * The viewing modes serve dual purposes:
 * 1. **Entertainment** - Engaging UI during analysis
 * 2. **Priority Signaling** - Paid users get faster perceived service
 *
 * ## Three Viewing Modes (by duration, longest to shortest):
 *
 * | Mode            | Duration | Target Users           | Purpose                        |
 * |-----------------|----------|------------------------|--------------------------------|
 * | Enjoy the Show  | 8s       | Free/Anonymous users   | Entertainment buffer (LONGEST) |
 * | VIP Experience  | 4s       | DramaX premium users   | Premium content, faster reveal |
 * | Skip to Results | 0s       | Paid users (opt-in)    | Instant results (FASTEST)      |
 *
 * ## Key Principle
 * "Enjoy the Show" is the LONGEST experience, creating a natural priority
 * where paid users (VIP, Skip) receive faster service. VIP focuses on
 * EXCLUSIVE CONTENT (premium messaging, foreshadowing, bigger badge reveal)
 * rather than longer duration.
 *
 * ## Future: Backend Priority Queue
 * Animation timing is currently UI-only. Future enhancement will add actual
 * backend priority hints to Edge Functions for real queue prioritization.
 *
 * @see /packages/drama-detector-docs/features/viewing-modes/
 * @see docs/VIEWING_MODE_PRIORITY_MODEL.md
 */

import type { TierId } from '@dramadetector/shared';

/**
 * Viewing mode variant identifier
 */
export type ViewingModeVariant = 'quick' | 'theatrical' | 'dramax_premium';

/**
 * @deprecated Use ViewingModeVariant instead. This alias will be removed in a future version.
 * Legacy alias for backward compatibility during transition to entertainment-focused naming.
 */
export type TheatricalModeVariant = ViewingModeVariant;

/**
 * Suspense phase configuration
 */
export interface SuspenseConfig {
  /** Whether suspense phase is enabled */
  enabled: boolean;

  /** Duration of the suspense crawl (92% → 99%) in milliseconds */
  crawlDuration: number;

  /** Duration of the vacuum freeze before badge reveal in milliseconds */
  vacuumFreeze: number;

  /** Messages to rotate during suspense phase */
  messages: string[];

  /** Interval between message rotations in milliseconds */
  messageRotationInterval: number;

  /**
   * Accelerating heartbeat intervals in milliseconds
   * Each value represents the duration of one heartbeat cycle.
   * Array progresses from slower to faster for dramatic tension.
   */
  heartbeatIntervals?: number[];

  /** Breathing pulse animation config */
  breathing: {
    min: number;  // Scale minimum (e.g., 0.98)
    max: number;  // Scale maximum (e.g., 1.02)
    duration: number;  // Duration per pulse cycle in ms
  };
}

/**
 * Badge reveal configuration
 */
export interface BadgeRevealConfig {
  /** Scale multiplier for badge entrance animation */
  scaleMultiplier: number;

  /** Whether to show visual foreshadowing glimpses */
  foreshadowing: boolean;
}

/**
 * Complete viewing mode configuration
 */
export interface ViewingModeConfig {
  /** Variant identifier */
  variant: ViewingModeVariant;

  /** Display name for UI */
  displayName: string;

  /** Description shown in selector */
  description: string;

  /** Icon name (Lucide React Native) */
  icon: 'Zap' | 'Sparkles' | 'Crown';

  /** Suspense phase configuration */
  suspense: SuspenseConfig;

  /** Badge reveal configuration */
  badgeReveal: BadgeRevealConfig;

  /** Tier-specific variant (e.g., VIP Experience shows different messages) */
  tierSpecific?: boolean;
}

/**
 * @deprecated Use ViewingModeConfig instead. This alias will be removed in a future version.
 * Legacy alias for backward compatibility during transition to entertainment-focused naming.
 */
export type TheatricalModeConfig = ViewingModeConfig;

/**
 * Skip to Results - Viewing Mode Configuration
 *
 * FASTEST mode - immediate results with no theatrical delay.
 * Available to paid users (Drama Plus, DramaX) as opt-in fast-track.
 *
 * Duration: 0 seconds (INSTANT)
 *
 * Features:
 * - No suspense phase (disabled)
 * - Minimal indeterminate loader (SkipToResultsLoader component)
 * - Auto-navigate to results the moment API responds
 * - Clean, efficient, no-nonsense experience
 *
 * Target Users:
 * - Paid tier users (Drama Plus, DramaX) who opt-in
 * - Power users (>20 analyses/week)
 * - Users with reduced motion enabled (accessibility)
 *
 * Purpose: Maximum efficiency for users who value speed over entertainment.
 * This is the ultimate priority fast-track.
 */
const QUICK_MODE_CONFIG: ViewingModeConfig = {
  variant: 'quick',
  displayName: 'Skip to Results',
  description: 'Get results immediately',
  icon: 'Zap',
  suspense: {
    enabled: false,
    crawlDuration: 0,
    vacuumFreeze: 0,
    messages: [],
    messageRotationInterval: 0,
    breathing: {
      min: 1.0,
      max: 1.0,
      duration: 0,
    },
  },
  badgeReveal: {
    scaleMultiplier: 1.0,
    foreshadowing: false,
  },
};

/**
 * Enjoy the Show - Viewing Mode Configuration
 *
 * DEFAULT mode for free/anonymous users. This is intentionally the LONGEST
 * experience to create natural priority for paid users.
 *
 * Duration: 8 seconds (LONGEST of all modes)
 *
 * Features:
 * - 8-second suspense crawl (92% → 99%) - entertainment buffer
 * - 6 rotating teaser messages every 1.3s
 * - 0.2s vacuum freeze before reveal
 * - Accelerating heartbeat animation (1.6s → 1.3s → 1.0s → 0.8s)
 * - Standard badge reveal (1.0x scale)
 *
 * Target Users:
 * - Free tier users
 * - Anonymous users
 * - New users (0-2 analyses)
 *
 * Purpose: Engaging theatrical experience that also serves as a "waiting room"
 * to naturally prioritize paid user requests.
 */
const THEATRICAL_MODE_CONFIG: ViewingModeConfig = {
  variant: 'theatrical',
  displayName: 'Enjoy the Show',
  description: 'Full theatrical experience',
  icon: 'Sparkles',
  suspense: {
    enabled: true,
    crawlDuration: 8000, // 8 seconds - LONGEST mode (priority buffer for free users)
    vacuumFreeze: 200,
    messages: [
      'Analyzing patterns...',
      'Detecting influences...',
      'Synthesizing insights...',
      'Evaluating dynamics...',
      'Calibrating results...',
      'Almost there...',
    ],
    messageRotationInterval: 1300, // Rotate every 1.3s (6 messages × 1.3s ≈ 8s)
    heartbeatIntervals: [1600, 1300, 1000, 800], // 4 beats, accelerating
    breathing: {
      min: 0.98,
      max: 1.02,
      duration: 1200,
    },
  },
  badgeReveal: {
    scaleMultiplier: 1.0,
    foreshadowing: false,
  },
};

/**
 * VIP Experience - Viewing Mode Configuration
 *
 * EXCLUSIVE mode for DramaX premium tier. Faster than free tier with
 * enhanced CONTENT (not longer duration). Premium = faster + exclusive.
 *
 * Duration: 4 seconds (50% faster than Enjoy the Show)
 *
 * Premium Features (CONTENT-focused, not duration):
 * - 4-second suspense crawl (FASTER than free tier's 8s)
 * - Premium exclusive messaging (3 messages)
 * - 0.15s vacuum freeze (snappier reveal)
 * - 1.3x badge reveal scale (30% larger, more dramatic entrance)
 * - Visual foreshadowing glimpses during suspense
 * - Rapid heartbeat acceleration (1.0s → 0.6s → 0.4s)
 * - Enhanced breathing pulse (more noticeable)
 *
 * Target Users:
 * - DramaX tier subscribers only
 *
 * Purpose: Reward premium users with FASTER service AND exclusive content.
 * The value proposition is speed + exclusivity, not longer wait times.
 */
const DRAMAX_PREMIUM_MODE_CONFIG: ViewingModeConfig = {
  variant: 'dramax_premium',
  displayName: 'VIP Experience',
  description: 'Premium fast-track with exclusive reveal',
  icon: 'Crown',
  suspense: {
    enabled: true,
    crawlDuration: 4000, // 4 seconds - FASTER than free (premium = priority)
    vacuumFreeze: 150, // Snappier reveal (0.15s vs 0.2s)
    messages: [
      'Premium analysis engaged...',
      'Decoding your patterns...',
      'Preparing VIP revelation...',
    ],
    messageRotationInterval: 1300, // 3 messages × 1.3s ≈ 4s
    heartbeatIntervals: [1000, 600, 400], // Rapid acceleration (premium urgency)
    breathing: {
      min: 0.95,
      max: 1.05, // More pronounced pulse (premium feel)
      duration: 800, // Faster breathing (energy)
    },
  },
  badgeReveal: {
    scaleMultiplier: 1.3, // 30% larger badge entrance (VIP dramatic reveal)
    foreshadowing: true, // Exclusive: glimpses during suspense
  },
  tierSpecific: true,
};

/**
 * All viewing mode configurations
 */
export const VIEWING_MODE_CONFIGS: Record<ViewingModeVariant, ViewingModeConfig> = {
  quick: QUICK_MODE_CONFIG,
  theatrical: THEATRICAL_MODE_CONFIG,
  dramax_premium: DRAMAX_PREMIUM_MODE_CONFIG,
};

/**
 * @deprecated Use VIEWING_MODE_CONFIGS instead. This alias will be removed in a future version.
 * Legacy alias for backward compatibility during transition to entertainment-focused naming.
 */
export const THEATRICAL_MODE_CONFIGS = VIEWING_MODE_CONFIGS;

/**
 * Adaptive logic rules for default viewing mode selection
 *
 * ## Priority Model
 * Free users default to longest wait (Enjoy the Show = 8s)
 * Paid users get faster service (VIP = 4s, Skip = 0s)
 *
 * ## Rules (in priority order):
 * 1. Reduced motion enabled → Skip to Results (accessibility override)
 * 2. DramaX tier → VIP Experience (4s, premium content)
 * 3. New users (0-2 analyses) → Enjoy the Show (8s, engagement)
 * 4. Power users (>20/week in first week) → Skip to Results (efficiency)
 * 5. Everyone else → Enjoy the Show (8s, default for free tier)
 * 6. Manual override → Remember user choice (AsyncStorage)
 *
 * ## Duration by Default Assignment:
 * | User Type          | Default Mode      | Duration |
 * |--------------------|-------------------|----------|
 * | Free/Anonymous     | Enjoy the Show    | 8s       |
 * | Drama Plus         | Enjoy the Show*   | 8s       |
 * | DramaX             | VIP Experience    | 4s       |
 * | Reduced Motion     | Skip to Results   | 0s       |
 *
 * *Drama Plus users can manually select Skip to Results for 0s
 */
export interface AdaptiveContext {
  /** User's subscription tier */
  tier: TierId;

  /** Total analysis count for the user */
  analysisCount: number;

  /** Analyses performed in the last 7 days */
  analysisCountLast7Days?: number;

  /** Whether user has reduced motion enabled */
  reducedMotionEnabled: boolean;

  /** Days since user registration */
  daysSinceRegistration?: number;
}

/**
 * Get default viewing mode based on adaptive logic
 *
 * @param context - User context for adaptive decision
 * @returns Appropriate viewing mode variant
 */
export function getDefaultTheatricalMode(context: AdaptiveContext): TheatricalModeVariant {
  // Rule 4: Reduced motion always gets quick mode
  if (context.reducedMotionEnabled) {
    return 'quick';
  }

  // Rule 3: DramaX tier gets premium experience
  if (context.tier === 'dramax') {
    return 'dramax_premium';
  }

  // Rule 1: New users (0-2 analyses) get theatrical mode
  if (context.analysisCount <= 2) {
    return 'theatrical';
  }

  // Rule 2: Power users (>20 analyses in first week) get quick mode
  if (
    context.daysSinceRegistration !== undefined &&
    context.daysSinceRegistration <= 7 &&
    context.analysisCountLast7Days !== undefined &&
    context.analysisCountLast7Days > 20
  ) {
    return 'quick';
  }

  // Rule 5: Everyone else defaults to theatrical mode (Enjoy the Show)
  return 'theatrical';
}

/**
 * Get viewing mode config by variant
 *
 * @param variant - Viewing mode variant
 * @returns Configuration object
 */
export function getTheatricalModeConfig(variant: TheatricalModeVariant): TheatricalModeConfig {
  return THEATRICAL_MODE_CONFIGS[variant];
}
